import React from 'react';
import { AdminSettings, AircraftType, Airstrip } from '../types';
import { useAdminPortal } from '../hooks/useAdminPortal';
import { PolicyTab } from './AdminPortal/PolicyTab';
import { FleetTab } from './AdminPortal/FleetTab';
import { AirstripsTab } from './AdminPortal/AirstripsTab';
import { RoutesTab } from './AdminPortal/RoutesTab';
import { RouteMatrixTab } from './AdminPortal/RouteMatrixTab';
import { FuelTab } from './AdminPortal/FuelTab';
import { CommercialTermsTab } from './AdminPortal/CommercialTermsTab';
import { ReservationCodesTab } from './AdminPortal/ReservationCodesTab';
import { ServicesTab } from './AdminPortal/ServicesTab';
import { ScheduledSopTab } from './AdminPortal/ScheduledSopTab';

interface AdminPortalProps {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  aircrafts: AircraftType[];
  setAircrafts: (a: AircraftType[]) => void;
  airstrips: Airstrip[];
  setAirstrips: (a: Airstrip[]) => void;
}

const AdminPortal: React.FC<AdminPortalProps> = ({ settings, setSettings, aircrafts, setAircrafts, airstrips, setAirstrips }) => {
  const {
    subTab,
    setSubTab,
    isSaving,
    saveStatus,
    saveSettings,
    showAirstripModal,
    editingAirstrip,
    airstripForm,
    setAirstripForm,
    isSavingAirstrip,
    isImportingAirstrips,
    airstripImportSummary,
    airstripDistances,
    distanceImportSummary,
    isImportingDistances,
    handleOpenAirstripModal,
    handleCloseAirstripModal,
    handleSaveAirstrip,
    handleDeleteAirstrip,
    handleDeleteAllAirstrips,
    handleDownloadAirstripTemplate,
    handleImportAirstripsFile,
    handleCloseAirstripImportSummary,
    handleImportAirstripDistances,
    handleUpdateAirstripDistance,
    handleDeleteAirstripDistance,
    routes,
    isImportingRoutes,
    routeImportSummary,
    isAddingRoute,
    newRoute,
    handleOpenAddRoute,
    handleCloseAddRoute,
    handleUpdateNewRoute,
    handleToggleRouteDay,
    handleCreateRoute,
    handleDeleteRoute,
    handleDeleteAllRoutes,
    handleUpdateRouteRate,
    handleUpdateRouteFields,
    handleImportRoutesFile,
    handleCloseRouteImportSummary,
    handleUpsertRouteAircraftRate,
    routeMatrixAircraftId,
    handleSelectRouteMatrixAircraft,
    handleUpdateRouteDefaults,
    handleUpdateAircraftRoutePricing,
    isLinkingFuel,
    fuelLinkNode,
    isSyncingFuel,
    showSyncReview,
    handleOpenLinkFuel,
    handleCloseLinkFuel,
    handleUpdateFuelLinkNode,
    handleLinkFuelNode,
    handleDecommissionFuelNode,
    handleUpdateFuelPriceInline,
    handleTriggerFuelSync,
    handleCloseSyncReview,
    handleCommitFuelSync,
    rollbackAgentSnapshot,
  } = useAdminPortal({ settings, setSettings, setAirstrips, setAircrafts, aircrafts, airstrips });

  React.useEffect(() => {
    try {
      const intent = localStorage.getItem('ma_admin_open_subtab');
      if (!intent) return;
      const normalized = String(intent || '').trim().toUpperCase();
      const valid = ['POLICY', 'SCHEDULED_SOP', 'FLEET', 'AIRSTRIPS', 'SERVICES', 'ROUTES', 'ROUTE_MATRIX', 'FUEL', 'RESERVATION_CODES', 'COMMERCIAL_TERMS'];
      if (valid.includes(normalized)) {
        setSubTab(normalized as any);
      }
      localStorage.removeItem('ma_admin_open_subtab');
    } catch {
      // ignore storage failures
    }
  }, [setSubTab]);

  const tabLabels: Record<string, string> = {
    POLICY: 'Policy',
    SCHEDULED_SOP: 'Scheduled SOP',
    FLEET: 'Fleet',
    AIRSTRIPS: 'Airstrips',
    SERVICES: 'Services',
    ROUTES: 'Routes',
    ROUTE_MATRIX: 'Route Matrix',
    FUEL: 'Fuel',
    RESERVATION_CODES: 'Reservation Codes',
    COMMERCIAL_TERMS: 'Commercial Terms',
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-16 overflow-x-hidden">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 border-b-2 border-slate-200/50 pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-slate-900 tracking-tight">System Registry</h2>
              <p className="text-sm text-slate-500 mt-0.5 font-medium">Enterprise Architecture & Governance</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap bg-gradient-to-r from-slate-50 to-blue-50/30 rounded-2xl p-1.5 border border-slate-200/50 shadow-sm gap-1.5 w-full lg:w-auto">
          {(['POLICY', 'SCHEDULED_SOP', 'FLEET', 'AIRSTRIPS', 'SERVICES', 'ROUTES', 'ROUTE_MATRIX', 'FUEL', 'RESERVATION_CODES', 'COMMERCIAL_TERMS'] as const).map(t => (
            <button
              key={t}
              onClick={() => setSubTab(t)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                subTab === t 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/40 scale-105' 
                  : 'text-slate-600 hover:text-blue-700 hover:bg-white/80 hover:shadow-md'
              }`}
            >
              {tabLabels[t]}
            </button>
          ))}
        </div>
      </div>

      {subTab === 'POLICY' && (
        <PolicyTab settings={settings} setSettings={setSettings} saveSettings={saveSettings} />
      )}

      {subTab === 'SCHEDULED_SOP' && (
        <ScheduledSopTab settings={settings} setSettings={setSettings} saveSettings={saveSettings} />
      )}


      {subTab === 'FLEET' && (
        <FleetTab aircrafts={aircrafts} setAircrafts={setAircrafts} />
      )}

      {subTab === 'AIRSTRIPS' && (
        <AirstripsTab
          airstrips={airstrips}
          showAirstripModal={showAirstripModal}
          editingAirstrip={editingAirstrip}
          airstripForm={airstripForm}
          setAirstripForm={setAirstripForm}
          isSavingAirstrip={isSavingAirstrip}
          isImporting={isImportingAirstrips}
          importSummary={airstripImportSummary}
          onOpenModal={handleOpenAirstripModal}
          onCloseModal={handleCloseAirstripModal}
          onSave={handleSaveAirstrip}
          onDelete={handleDeleteAirstrip}
          onDeleteAll={handleDeleteAllAirstrips}
          onDownloadTemplate={handleDownloadAirstripTemplate}
          onImportFile={handleImportAirstripsFile}
          onCloseImportSummary={handleCloseAirstripImportSummary}
        />
      )}

      {subTab === 'SERVICES' && (
        <ServicesTab airstrips={airstrips} aircrafts={aircrafts} />
      )}

      {subTab === 'ROUTES' && (
        <RoutesTab
          airstrips={airstrips}
          aircrafts={aircrafts}
          routes={routes}
          isImportingRoutes={isImportingRoutes}
          routeImportSummary={routeImportSummary}
          isAddingRoute={isAddingRoute}
          newRoute={newRoute}
          onOpenAddRoute={handleOpenAddRoute}
          onCloseAddRoute={handleCloseAddRoute}
          onUpdateNewRoute={handleUpdateNewRoute}
          onToggleRouteDay={handleToggleRouteDay}
          onCreateRoute={handleCreateRoute}
          onDeleteRoute={handleDeleteRoute}
          onDeleteAllRoutes={handleDeleteAllRoutes}
          onUpdateRouteRate={handleUpdateRouteRate}
          onUpdateRouteFields={handleUpdateRouteFields}
          onImportRoutesFile={handleImportRoutesFile}
          onCloseRouteImportSummary={handleCloseRouteImportSummary}
          onUpsertRouteAircraftRate={handleUpsertRouteAircraftRate}
        />
      )}

      {subTab === 'ROUTE_MATRIX' && (
        <RouteMatrixTab
          airstrips={airstrips}
          aircrafts={aircrafts}
          settings={settings}
          distances={airstripDistances}
          distanceImportSummary={distanceImportSummary}
          isImportingDistances={isImportingDistances}
          selectedAircraftId={routeMatrixAircraftId}
          onSelectAircraft={handleSelectRouteMatrixAircraft}
          onUpdateRouteDefaults={handleUpdateRouteDefaults}
          onUpdateAircraftRoutePricing={handleUpdateAircraftRoutePricing}
          onImportDistances={handleImportAirstripDistances}
          onUpdateDistance={handleUpdateAirstripDistance}
          onDeleteDistance={handleDeleteAirstripDistance}
        />
      )}

      {subTab === 'FUEL' && (
        <FuelTab
          airstrips={airstrips}
          isLinkingFuel={isLinkingFuel}
          fuelLinkNode={fuelLinkNode}
          isSyncingFuel={isSyncingFuel}
          showSyncReview={showSyncReview}
          onOpenLinkFuel={handleOpenLinkFuel}
          onCloseLinkFuel={handleCloseLinkFuel}
          onUpdateFuelLinkNode={handleUpdateFuelLinkNode}
          onLinkFuelNode={handleLinkFuelNode}
          onDecommissionFuelNode={handleDecommissionFuelNode}
          onUpdateFuelPriceInline={handleUpdateFuelPriceInline}
          onTriggerFuelSync={handleTriggerFuelSync}
          onCloseSyncReview={handleCloseSyncReview}
          onCommitFuelSync={handleCommitFuelSync}
        />
      )}

      {subTab === 'RESERVATION_CODES' && (
        <ReservationCodesTab
          settings={settings}
          onUpdateSettings={(updates) => {
            const next = { ...settings, ...updates };
            setSettings(next);
            saveSettings(next);
          }}
        />
      )}

      {subTab === 'COMMERCIAL_TERMS' && (
        <CommercialTermsTab />
      )}


    </div>
  );
};

export default AdminPortal;
